/**
 * Accelerators & Tools — ready-to-run applications built on TurfAI.
 * One typed array drives routing, SEO, the nav menu and the shared <AcceleratorPage>.
 * Copy: docs/copy/2026-10-page-copy.md (Accelerators & Tools).
 */

export interface AcceleratorProduct {
  name: string;
  line: string;
  points: string[];
  audience?: string;
}

export interface Accelerator {
  /** route path, e.g. "/stageone" */
  path: string;
  /** page id used by onNavigate */
  pageId: string;
  name: string;
  /** one-liner for menus */
  menuDesc: string;
  eyebrow: string;
  headline: string;
  subhead: string;
  visual: "stageone" | "learning" | "promptbench";
  powersHeadline: string;
  powers: string[];
  capabilitiesHeadline: string;
  capabilities: { title: string; body: string }[];
  /** learning tools: the three products in the family */
  products?: AcceleratorProduct[];
  proof: string;
  related: { page: string; label: string; desc: string }[];
  metaTitle: string;
  metaDescription: string;
}

export const accelerators: Accelerator[] = [
  {
    path: "/stageone",
    pageId: "stageone",
    name: "StageOne",
    menuDesc: "AI hiring assessments",
    eyebrow: "StageOne · AI hiring assessments",
    headline: "Technical hiring that scores every candidate the same way.",
    subhead:
      "StageOne screens applicants against the role, runs an adaptive test with multiple-choice, spoken and coding questions, and hands recruiters a scored report — every AI step running on TurfAI.",
    visual: "stageone",
    powersHeadline: "How TurfAI powers StageOne",
    powers: [
      "Job descriptions and resumes are parsed by TurfAI workflows, then scored for fit on HR and technical criteria.",
      "One TurfAI workflow generates every question type; prompts are versioned in TurfAI, not hard-coded in the app.",
      "Answers are evaluated and given written feedback by TurfAI, with the model swappable between providers.",
      "Integrity signals from proctoring are summarised into a read-out the recruiter can check.",
    ],
    capabilitiesHeadline: "Everything a first round needs",
    capabilities: [
      { title: "Fit before the test", body: "A match score from the resume and the job description decides who is invited." },
      { title: "Adaptive assessment", body: "Difficulty moves with each answer, so strong candidates are stretched and the test stays short." },
      { title: "Three question types", body: "Multiple choice, spoken answers and coding, with code run and checked automatically." },
      { title: "Scored reports", body: "Per-answer scores, written feedback and a hiring recommendation." },
      { title: "Integrity read-out", body: "Screen and audio recording with flagged moments, not a black-box verdict." },
      { title: "API", body: "Other systems can request an assessment and read the result." },
    ],
    proof: "In production — every candidate assessed the same way, and recruiters shortlist in hours, not weeks.",
    related: [
      { page: "solutions-hr-tech", label: "HR Tech", desc: "Hiring, onboarding and employee support on TurfAI." },
      { page: "platform-agents", label: "AI Agents", desc: "The agents that parse, score and evaluate." },
    ],
    metaTitle: "StageOne — AI hiring assessments | TurfAI",
    metaDescription:
      "StageOne screens applicants against the role, runs adaptive multiple-choice, spoken and coding assessments, and hands recruiters a scored report — built on TurfAI.",
  },
  {
    path: "/learning",
    pageId: "learning",
    name: "Learning tools",
    menuDesc: "SkillUp, DataLab and FishBone",
    eyebrow: "Learning tools · SkillUp, DataLab, FishBone",
    headline: "Practise, build, reason — AI learning tools that make students think.",
    subhead:
      "Three tools that cover the learning loop: SkillUp for practice and assessment, DataLab for hands-on data science, and FishBone, a Socratic mentor that guides instead of answering — orchestrated on TurfAI.",
    visual: "learning",
    powersHeadline: "How TurfAI powers the learning tools",
    powers: [
      "FishBone's reasoning runs through TurfAI workflows, with templates for tutoring, troubleshooting and decisions.",
      "SkillUp generates and validates questions from learning objectives through TurfAI.",
      "DataLab launches sandboxed notebooks from the LMS, with TurfAI assistance beside the work.",
      "FishBone embeds inside SkillUp and DataLab, so a stuck learner gets a mentor where they are.",
    ],
    capabilitiesHeadline: "One family, the whole learning loop",
    capabilities: [],
    products: [
      {
        name: "SkillUp",
        line: "Practice that knows what each student should learn next.",
        points: [
          "AI-generated questions from learning objectives and Bloom's levels, validated for format, maths notation and duplicates",
          "Ten-plus question types — multiple choice, match, order, cloze, label-the-diagram",
          "Instant grading with explanations",
          "Weak-area detection and what to practise next",
          "Teacher quiz builder, class management and a mastery heatmap",
          "A review queue for generated content, plus badges, streaks and leaderboards",
        ],
        audience: "Grades 8–12 and competitive-exam preparation",
      },
      {
        name: "DataLab",
        line: "One click from the course to a ready notebook with the data already there.",
        points: [
          "Launch from Brightspace or Moodle, with rosters and grades synced (LTI 1.3)",
          "A sandboxed notebook per student",
          "Shared datasets mounted, not copied",
          "Projects with checkpoints and rubrics",
          "Grading, gradebook and extensions",
          "Announcements, messages and forums",
        ],
        audience: "Universities and course teams teaching data science",
      },
      {
        name: "FishBone",
        line: "An AI mentor that asks the next question instead of giving the answer.",
        points: [
          "Guided questioning from reasoning templates",
          "Hints that escalate step by step, only when the learner is stuck",
          "Misconception detection",
          "Up to three alternative lines of reasoning, compared side by side",
          "Assessments built from a session, graded by AI",
          "Learning insights, and embeddable in other apps with an API key",
        ],
        audience: "Learners, instructors and the apps they already use",
      },
    ],
    proof: "Students who practise with feedback, build in real labs and reason with a mentor — and teachers who see where every learner is stuck before the exam does.",
    related: [
      { page: "solutions-education", label: "Education", desc: "Admissions and learning on TurfAI." },
      { page: "platform-chatbots", label: "Chatbots", desc: "The conversational layer behind FishBone." },
    ],
    metaTitle: "Learning tools — SkillUp, DataLab, FishBone | TurfAI",
    metaDescription:
      "SkillUp for adaptive practice, DataLab for data-science labs inside the LMS, and FishBone, a Socratic AI mentor — the learning loop, orchestrated on TurfAI.",
  },
  {
    path: "/promptbench",
    pageId: "promptbench",
    name: "PromptBench",
    menuDesc: "AI model and prompt evaluation",
    eyebrow: "PromptBench · AI model and prompt evaluation",
    headline: "Pick the right model. Prove the right prompt.",
    subhead:
      "PromptBench runs the same test cases across OpenAI, Anthropic, Google and Azure OpenAI models, scores every answer automatically, routes the close calls to human reviewers and shows cost beside quality — so each TurfAI workflow step runs on the model that earns it.",
    visual: "promptbench",
    powersHeadline: "How it works with TurfAI",
    powers: [
      "Design a prompt in TurfAI's Prompt Lab.",
      "Prove it in PromptBench — every version, every model, every test case.",
      "Publish it through TurfAI's prompt review, with the score card attached.",
    ],
    capabilitiesHeadline: "Write, test, score, review, compare",
    capabilities: [
      { title: "Automatic scoring", body: "Similarity to the expected answer, required keywords, valid JSON and length, against a pass mark." },
      { title: "Human review", body: "Eight review dimensions, reviewer assignment and agreement tracking between reviewers." },
      { title: "Versions side by side", body: "Word-level diffs between prompt versions, with results for each." },
      { title: "Models side by side", body: "The same tests across OpenAI, Anthropic, Google and Azure OpenAI, with token cost beside quality." },
      { title: "Free dry runs", body: "Mock runs to build test suites before spending on live models." },
      { title: "Chains, roles and audit", body: "Multi-stage prompt workflows tested end to end; six roles and a full audit log." },
    ],
    proof: "Every prompt ships with a score card — and teams often find a smaller, cheaper model passes the same tests.",
    related: [
      { page: "platform-governance", label: "Governance & Controls", desc: "Prompt review and publish-time checks." },
      { page: "platform-edge-ai", label: "Edge AI", desc: "Small models, measured before you switch." },
    ],
    metaTitle: "PromptBench — AI model and prompt evaluation | TurfAI",
    metaDescription:
      "PromptBench tests prompts across OpenAI, Anthropic, Google and Azure OpenAI models, scores every answer, routes close calls to reviewers and shows cost beside quality.",
  },
];
